import { BuiltinDoorId } from "building-types/doors/BuiltinDoorId";
import { DoorOperationType } from "building-types/doors/doorOperationTypes";
import type { DoorDescriptor } from "building-types/doors/doorTypes";
import { DoorPropIdPrefix, SwingSide } from "building-types/doors/doorTypes";
import { BuiltInExteriorPropId } from "building-types/props/builtInExteriorPropId";
import type { DoorCatalogSettings } from "building-types/settings/doorCatalogSettings";
import { GARAGE_TWO_WIDTH } from "common/constants/buildingObjects";
import type { Table } from "common/data/table";
import { levelInchesNew } from "common/utils/nominalNumbers";
import objectIsEmpty from "common/utils/objectIsEmpty";
import { getDescriptorName } from "../../catalogType";
import { BuiltInInteriorPropId } from "../propCatalog/interiorPropCatalog";
import { doorDescriptorNew } from "./doorDescriptors";

export function getDoorDescriptorName(descriptor: DoorDescriptor): string | undefined {
  return getDescriptorName(descriptor, BuiltinDoorId);
}

export const defaultDoorCatalog: { [key in BuiltinDoorId]: DoorDescriptor } = {
  INT_16XX: doorDescriptorNew({
    id: BuiltinDoorId.INT_16XX,
    reportedName: "16/XX",
    propIdPrefix: DoorPropIdPrefix.DoorInterior,
    width: levelInchesNew(18),
    includeTrim: true,
    requiresHeightComputation: true,
  }),
  INT_18XX: doorDescriptorNew({
    id: BuiltinDoorId.INT_18XX,
    reportedName: "18/XX",
    propIdPrefix: DoorPropIdPrefix.DoorInterior,
    width: levelInchesNew(20),
    includeTrim: true,
    requiresHeightComputation: true,
  }),
  INT_1868: doorDescriptorNew({
    id: BuiltinDoorId.INT_1868,
    reportedName: "18/68",
    propIdPrefix: DoorPropIdPrefix.DoorInterior,
    width: levelInchesNew(20),
    height: levelInchesNew(80),
    includeTrim: true,
  }),
  INT_1880: doorDescriptorNew({
    id: BuiltinDoorId.INT_1880,
    reportedName: "18/80",
    propIdPrefix: DoorPropIdPrefix.DoorInterior,
    width: levelInchesNew(20),
    height: levelInchesNew(96),
    includeTrim: true,
  }),
  INT_20XX: doorDescriptorNew({
    id: BuiltinDoorId.INT_20XX,
    reportedName: "20/XX",
    propIdPrefix: DoorPropIdPrefix.DoorInterior,
    width: levelInchesNew(24),
    includeTrim: true,
    requiresHeightComputation: true,
  }),
  INT_2050: doorDescriptorNew({
    id: BuiltinDoorId.INT_2050,
    reportedName: "20/50",
    propIdPrefix: DoorPropIdPrefix.DoorInterior,
    operationType: DoorOperationType.ATTIC,
    width: levelInchesNew(24),
    height: levelInchesNew(60),
  }),
  INT_2068: doorDescriptorNew({
    id: BuiltinDoorId.INT_2068,
    reportedName: "20/68",
    propIdPrefix: DoorPropIdPrefix.DoorInterior,
    width: levelInchesNew(24),
    height: levelInchesNew(80),
  }),
  INT_24XX: doorDescriptorNew({
    id: BuiltinDoorId.INT_24XX,
    reportedName: "24/XX",
    propIdPrefix: DoorPropIdPrefix.DoorInterior,
    width: levelInchesNew(28),
    includeTrim: true,
    requiresHeightComputation: true,
  }),
  INT_2468: doorDescriptorNew({
    id: BuiltinDoorId.INT_2468,
    reportedName: "24/68",
    propIdPrefix: DoorPropIdPrefix.DoorInterior,
    width: levelInchesNew(28),
    height: levelInchesNew(80),
    includeTrim: true,
  }),
  INT_2480: doorDescriptorNew({
    id: BuiltinDoorId.INT_2480,
    reportedName: "24/80",
    propIdPrefix: DoorPropIdPrefix.DoorInterior,
    width: levelInchesNew(28),
    height: levelInchesNew(96),
    includeTrim: true,
  }),
  INT_2080: doorDescriptorNew({
    id: BuiltinDoorId.INT_2080,
    reportedName: "20/80",
    propIdPrefix: DoorPropIdPrefix.DoorInterior,
    width: levelInchesNew(24),
    height: levelInchesNew(96),
    includeTrim: true,
  }),
  INT_2668: doorDescriptorNew({
    id: BuiltinDoorId.INT_2668,
    reportedName: "26/68",
    propIdPrefix: DoorPropIdPrefix.DoorInterior,
    width: levelInchesNew(30),
    height: levelInchesNew(80),
  }),
  INT_2680: doorDescriptorNew({
    id: BuiltinDoorId.INT_2680,
    reportedName: "26/80",
    propIdPrefix: DoorPropIdPrefix.DoorInterior,
    width: levelInchesNew(30),
    height: levelInchesNew(96),
    includeTrim: true,
  }),
  INT_26XX: doorDescriptorNew({
    id: BuiltinDoorId.INT_26XX,
    reportedName: "26/XX",
    propIdPrefix: DoorPropIdPrefix.DoorInterior,
    width: levelInchesNew(30),
    includeTrim: true,
    requiresHeightComputation: true,
  }),
  INT_2868: doorDescriptorNew({
    id: BuiltinDoorId.INT_2868,
    reportedName: "28/68",
    propIdPrefix: DoorPropIdPrefix.DoorInterior,
    width: levelInchesNew(32),
    height: levelInchesNew(80),
    includeTrim: true,
  }),
  INT_2880: doorDescriptorNew({
    id: BuiltinDoorId.INT_2880,
    reportedName: "28/80",
    propIdPrefix: DoorPropIdPrefix.DoorInterior,
    width: levelInchesNew(32),
    height: levelInchesNew(96),
    includeTrim: true,
  }),
  INT_28XX: doorDescriptorNew({
    id: BuiltinDoorId.INT_28XX,
    reportedName: "28/XX",
    propIdPrefix: DoorPropIdPrefix.DoorInterior,
    width: levelInchesNew(32),
    includeTrim: true,
    requiresHeightComputation: true,
  }),
  INT_30XX: doorDescriptorNew({
    id: BuiltinDoorId.INT_30XX,
    reportedName: "30/XX",
    propIdPrefix: DoorPropIdPrefix.DoorInterior,
    width: levelInchesNew(36),
    includeTrim: true,
    requiresHeightComputation: true,
  }),
  INT_3080: doorDescriptorNew({
    id: BuiltinDoorId.INT_3080,
    reportedName: "30/80",
    propIdPrefix: DoorPropIdPrefix.DoorInterior,
    width: levelInchesNew(36),
    height: levelInchesNew(96),
    includeTrim: true,
  }),
  INT_30XX_GARAGE: doorDescriptorNew({
    id: BuiltinDoorId.INT_30XX_GARAGE,
    reportedName: "20 MIN 30/XX",
    propIdPrefix: DoorPropIdPrefix.DoorInterior,
    width: levelInchesNew(36),
    includeTrim: true,
    requiresHeightComputation: true,
  }),
  INT_3068_GARAGE: doorDescriptorNew({
    id: BuiltinDoorId.INT_3068_GARAGE,
    reportedName: "20 MIN 30/68",
    propId: BuiltInInteriorPropId.DOORINTERIOR3068,
    width: levelInchesNew(36),
    height: levelInchesNew(80),
    includeTrim: true,
  }),
  INT_3068_GARAGE_SC: doorDescriptorNew({
    id: BuiltinDoorId.INT_3068_GARAGE_SC,
    reportedName: "20 MIN 30/68 \n SELF CLOSING",
    width: levelInchesNew(36),
    height: levelInchesNew(80),
  }),
  INT_32XX: doorDescriptorNew({
    id: BuiltinDoorId.INT_32XX,
    reportedName: "32/XX",
    propIdPrefix: DoorPropIdPrefix.DoorInterior,
    width: levelInchesNew(38),
    includeTrim: true,
    requiresHeightComputation: true,
  }),
  INTC_16XX: doorDescriptorNew({
    id: BuiltinDoorId.INTC_16XX,
    reportedName: "C16/XX",
    operationType: DoorOperationType.CLOSET,
    propIdPrefix: DoorPropIdPrefix.DoorInteriorCloset,
    width: levelInchesNew(18),
    includeTrim: true,
    requiresHeightComputation: true,
  }),
  INTC_18XX: doorDescriptorNew({
    id: BuiltinDoorId.INTC_18XX,
    reportedName: "C18/XX",
    operationType: DoorOperationType.CLOSET,
    propIdPrefix: DoorPropIdPrefix.DoorInteriorCloset,
    width: levelInchesNew(20),
    includeTrim: true,
    requiresHeightComputation: true,
  }),
  INTC_20XX: doorDescriptorNew({
    id: BuiltinDoorId.INTC_20XX,
    reportedName: "C20/XX",
    operationType: DoorOperationType.CLOSET,
    propIdPrefix: DoorPropIdPrefix.DoorInteriorCloset,
    width: levelInchesNew(24),
    includeTrim: true,
    requiresHeightComputation: true,
  }),
  INTC_24XX: doorDescriptorNew({
    id: BuiltinDoorId.INTC_24XX,
    reportedName: "C24/XX",
    operationType: DoorOperationType.CLOSET,
    propIdPrefix: DoorPropIdPrefix.DoorInteriorCloset,
    width: levelInchesNew(28),
    includeTrim: true,
    requiresHeightComputation: true,
  }),
  INTC_26XX: doorDescriptorNew({
    id: BuiltinDoorId.INTC_26XX,
    reportedName: "C26/XX",
    operationType: DoorOperationType.CLOSET,
    propIdPrefix: DoorPropIdPrefix.DoorInteriorCloset,
    width: levelInchesNew(30),
    includeTrim: true,
    requiresHeightComputation: true,
  }),
  INTC_2680: doorDescriptorNew({
    id: BuiltinDoorId.INTC_2680,
    reportedName: "C26/80",
    operationType: DoorOperationType.CLOSET,
    propIdPrefix: DoorPropIdPrefix.DoorInteriorCloset,
    width: levelInchesNew(30),
    height: levelInchesNew(96),
    includeTrim: true,
  }),
  INTC_28XX: doorDescriptorNew({
    id: BuiltinDoorId.INTC_28XX,
    operationType: DoorOperationType.CLOSET,
    reportedName: "C28/XX",
    propIdPrefix: DoorPropIdPrefix.DoorInteriorCloset,
    width: levelInchesNew(32),
    includeTrim: true,
    requiresHeightComputation: true,
  }),
  INTC_30XX: doorDescriptorNew({
    id: BuiltinDoorId.INTC_30XX,
    reportedName: "C30/XX",
    operationType: DoorOperationType.CLOSET,
    propIdPrefix: DoorPropIdPrefix.DoorInteriorCloset,
    width: levelInchesNew(36),
    includeTrim: true,
    requiresHeightComputation: true,
  }),
  INTC_32XX: doorDescriptorNew({
    id: BuiltinDoorId.INTC_32XX,
    reportedName: "C32/XX",
    operationType: DoorOperationType.CLOSET,
    propIdPrefix: DoorPropIdPrefix.DoorInteriorCloset,
    width: levelInchesNew(38),
    includeTrim: true,
    requiresHeightComputation: true,
  }),
  INTL_28XX: doorDescriptorNew({
    id: BuiltinDoorId.INTL_28XX,
    reportedName: "L28/XX",
    propIdPrefix: DoorPropIdPrefix.DoorInteriorLouvred,
    width: levelInchesNew(32),
    includeTrim: true,
    requiresHeightComputation: true,
  }),
  DBL_30XX: doorDescriptorNew({
    id: BuiltinDoorId.DBL_30XX,
    reportedName: "DBL 30/XX",
    propIdPrefix: DoorPropIdPrefix.DoorInteriorDouble,
    operationType: DoorOperationType.DOUBLE,
    width: levelInchesNew(36),
    doorLeafCount: 2,
    includeTrim: true,
    requiresHeightComputation: true,
  }),
  DBL_40XX: doorDescriptorNew({
    id: BuiltinDoorId.DBL_40XX,
    reportedName: "DBL 40/XX",
    propIdPrefix: DoorPropIdPrefix.DoorInteriorDouble,
    operationType: DoorOperationType.DOUBLE,
    width: levelInchesNew(48),
    doorLeafCount: 2,
    includeTrim: true,
    requiresHeightComputation: true,
  }),
  DBL_48XX: doorDescriptorNew({
    id: BuiltinDoorId.DBL_48XX,
    reportedName: "DBL 48/XX",
    propIdPrefix: DoorPropIdPrefix.DoorInteriorDouble,
    operationType: DoorOperationType.DOUBLE,
    width: levelInchesNew(56),
    doorLeafCount: 2,
    includeTrim: true,
    requiresHeightComputation: true,
  }),
  DBL_50XX: doorDescriptorNew({
    id: BuiltinDoorId.DBL_50XX,
    reportedName: "DBL 50/XX",
    propIdPrefix: DoorPropIdPrefix.DoorInteriorDouble,
    operationType: DoorOperationType.DOUBLE,
    width: levelInchesNew(60),
    doorLeafCount: 2,
    includeTrim: true,
    requiresHeightComputation: true,
  }),
  DBL_54XX: doorDescriptorNew({
    id: BuiltinDoorId.DBL_54XX,
    reportedName: "DBL 54/XX",
    propIdPrefix: DoorPropIdPrefix.DoorInteriorDouble,
    operationType: DoorOperationType.DOUBLE,
    width: levelInchesNew(64),
    doorLeafCount: 2,
    includeTrim: true,
    requiresHeightComputation: true,
  }),
  DBL_60XX: doorDescriptorNew({
    id: BuiltinDoorId.DBL_60XX,
    reportedName: "DBL 60/XX",
    propIdPrefix: DoorPropIdPrefix.DoorInteriorDouble,
    operationType: DoorOperationType.DOUBLE,
    width: levelInchesNew(72),
    doorLeafCount: 2,
    includeTrim: true,
    requiresHeightComputation: true,
  }),
  DBL_64XX: doorDescriptorNew({
    id: BuiltinDoorId.DBL_64XX,
    reportedName: "DBL 64/XX",
    propIdPrefix: DoorPropIdPrefix.DoorInteriorDouble,
    operationType: DoorOperationType.DOUBLE,
    width: levelInchesNew(76),
    doorLeafCount: 2,
    includeTrim: true,
    requiresHeightComputation: true,
  }),
  DBLC_30XX: doorDescriptorNew({
    id: BuiltinDoorId.DBLC_30XX,
    reportedName: "DBLC 30/XX",
    propIdPrefix: DoorPropIdPrefix.DoorInteriorDoubleCloset,
    operationType: DoorOperationType.DOUBLE,
    width: levelInchesNew(36),
    doorLeafCount: 2,
    includeTrim: true,
    requiresHeightComputation: true,
  }),
  DBLC_40XX: doorDescriptorNew({
    id: BuiltinDoorId.DBLC_40XX,
    reportedName: "DBLC 40/XX",
    propIdPrefix: DoorPropIdPrefix.DoorInteriorDoubleCloset,
    operationType: DoorOperationType.DOUBLE,
    width: levelInchesNew(48),
    doorLeafCount: 2,
    includeTrim: true,
    requiresHeightComputation: true,
  }),
  DBLC_48XX: doorDescriptorNew({
    id: BuiltinDoorId.DBLC_48XX,
    reportedName: "DBLC 48/XX",
    propIdPrefix: DoorPropIdPrefix.DoorInteriorDoubleCloset,
    operationType: DoorOperationType.DOUBLE,
    width: levelInchesNew(56),
    doorLeafCount: 2,
    includeTrim: true,
    requiresHeightComputation: true,
  }),
  DBLC_50XX: doorDescriptorNew({
    id: BuiltinDoorId.DBLC_50XX,
    reportedName: "DBLC 50/XX",
    propIdPrefix: DoorPropIdPrefix.DoorInteriorDoubleCloset,
    operationType: DoorOperationType.DOUBLE,
    width: levelInchesNew(60),
    doorLeafCount: 2,
    includeTrim: true,
    requiresHeightComputation: true,
  }),
  DBLC_54XX: doorDescriptorNew({
    id: BuiltinDoorId.DBLC_54XX,
    reportedName: "DBL 54/XX",
    propIdPrefix: DoorPropIdPrefix.DoorInteriorDoubleCloset,
    operationType: DoorOperationType.DOUBLE,
    width: levelInchesNew(64),
    doorLeafCount: 2,
    includeTrim: true,
    requiresHeightComputation: true,
  }),
  DBLC_60XX: doorDescriptorNew({
    id: BuiltinDoorId.DBLC_60XX,
    reportedName: "DBLC 60/XX",
    propIdPrefix: DoorPropIdPrefix.DoorInteriorDoubleCloset,
    operationType: DoorOperationType.DOUBLE,
    width: levelInchesNew(72),
    doorLeafCount: 2,
    includeTrim: true,
    requiresHeightComputation: true,
  }),
  DBLC_64XX: doorDescriptorNew({
    id: BuiltinDoorId.DBLC_64XX,
    reportedName: "DBLC 64/XX",
    propIdPrefix: DoorPropIdPrefix.DoorInteriorDoubleCloset,
    operationType: DoorOperationType.DOUBLE,
    width: levelInchesNew(76),
    doorLeafCount: 2,
    includeTrim: true,
    requiresHeightComputation: true,
  }),
  DBLF_50XX: doorDescriptorNew({
    id: BuiltinDoorId.DBLF_50XX,
    reportedName: "50/XX",
    propIdPrefix: DoorPropIdPrefix.DoorDoubleFrench,
    operationType: DoorOperationType.DOUBLE,
    width: levelInchesNew(60),
    doorLeafCount: 2,
    includeTrim: true,
    requiresHeightComputation: true,
  }),
  DBLF_50XX_15LITE: doorDescriptorNew({
    id: BuiltinDoorId.DBLF_50XX_15LITE,
    reportedName: "50/XX",
    propIdPrefix: DoorPropIdPrefix.DoorDoubleFrench15Lite,
    width: levelInchesNew(60),
    doorLeafCount: 2,
    includeTrim: true,
    requiresHeightComputation: true,
  }),
  DBLFT_6068: doorDescriptorNew({
    id: BuiltinDoorId.DBLFT_6068,
    reportedName: "60/68 W/ 60/10 TRANSOM",
    propId: BuiltInInteriorPropId.DOORDOUBLEFRENCHTRANSOM6068,
    operationType: DoorOperationType.DOUBLE,
    width: levelInchesNew(72),
    height: levelInchesNew(92),
    doorLeafCount: 2,
    includeTrim: true,
  }),
  BYP_30XX: doorDescriptorNew({
    id: BuiltinDoorId.BYP_30XX,
    reportedName: "BYP 30/XX",
    propIdPrefix: DoorPropIdPrefix.DoorInteriorDoubleBypass,
    operationType: DoorOperationType.BYPASS,
    width: levelInchesNew(36),
    doorLeafCount: 2,
    includeTrim: true,
    requiresHeightComputation: true,
  }),
  BYP_40XX: doorDescriptorNew({
    id: BuiltinDoorId.BYP_40XX,
    reportedName: "BYP 40/XX",
    propIdPrefix: DoorPropIdPrefix.DoorInteriorDoubleBypass,
    operationType: DoorOperationType.BYPASS,
    width: levelInchesNew(48),
    doorLeafCount: 2,
    includeTrim: true,
    requiresHeightComputation: true,
  }),
  BYP_4068: doorDescriptorNew({
    id: BuiltinDoorId.BYP_4068,
    reportedName: "BYP 40/68",
    propIdPrefix: DoorPropIdPrefix.DoorInteriorDoubleBypass,
    operationType: DoorOperationType.BYPASS,
    height: levelInchesNew(80),
    width: levelInchesNew(48),
    doorLeafCount: 2,
    includeTrim: true,
  }),
  BYP_48XX: doorDescriptorNew({
    id: BuiltinDoorId.BYP_48XX,
    reportedName: "BYP 48/XX",
    propIdPrefix: DoorPropIdPrefix.DoorInteriorDoubleBypass,
    operationType: DoorOperationType.BYPASS,
    width: levelInchesNew(56),
    doorLeafCount: 2,
    includeTrim: true,
    requiresHeightComputation: true,
  }),
  BYP_50XX: doorDescriptorNew({
    id: BuiltinDoorId.BYP_50XX,
    reportedName: "BYP 50/XX",
    propIdPrefix: DoorPropIdPrefix.DoorInteriorDoubleBypass,
    operationType: DoorOperationType.BYPASS,
    width: levelInchesNew(60),
    doorLeafCount: 2,
    includeTrim: true,
    requiresHeightComputation: true,
  }),
  BYP_54XX: doorDescriptorNew({
    id: BuiltinDoorId.BYP_54XX,
    reportedName: "BYP 54/XX",
    propIdPrefix: DoorPropIdPrefix.DoorInteriorDoubleBypass,
    operationType: DoorOperationType.BYPASS,
    width: levelInchesNew(64),
    doorLeafCount: 2,
    includeTrim: true,
    requiresHeightComputation: true,
  }),
  BYP_60XX: doorDescriptorNew({
    id: BuiltinDoorId.BYP_60XX,
    reportedName: "BYP 60/XX",
    propIdPrefix: DoorPropIdPrefix.DoorInteriorDoubleBypass,
    operationType: DoorOperationType.BYPASS,
    width: levelInchesNew(72),
    doorLeafCount: 2,
    includeTrim: true,
    requiresHeightComputation: true,
  }),
  BYP_64XX: doorDescriptorNew({
    id: BuiltinDoorId.BYP_64XX,
    reportedName: "BYP 64/XX",
    propIdPrefix: DoorPropIdPrefix.DoorInteriorDoubleBypass,
    operationType: DoorOperationType.BYPASS,
    width: levelInchesNew(76),
    doorLeafCount: 2,
    includeTrim: true,
    requiresHeightComputation: true,
  }),
  PKT_16XX: doorDescriptorNew({
    id: BuiltinDoorId.PKT_16XX,
    reportedName: "PKT 16/XX",
    propIdPrefix: DoorPropIdPrefix.DoorInteriorPocket,
    width: levelInchesNew(18),
    includeTrim: true,
    requiresDoorSwing: false,
    requiresHeightComputation: true,
  }),
  PKT_20XX: doorDescriptorNew({
    id: BuiltinDoorId.PKT_20XX,
    reportedName: "PKT 20/XX",
    propIdPrefix: DoorPropIdPrefix.DoorInteriorPocket,
    width: levelInchesNew(24),
    includeTrim: true,
    requiresDoorSwing: false,
    requiresHeightComputation: true,
  }),
  PKT_24XX: doorDescriptorNew({
    id: BuiltinDoorId.PKT_24XX,
    reportedName: "PKT 24/XX",
    propIdPrefix: DoorPropIdPrefix.DoorInteriorPocket,
    width: levelInchesNew(28),
    includeTrim: true,
    requiresDoorSwing: false,
    requiresHeightComputation: true,
  }),
  PKT_26XX: doorDescriptorNew({
    id: BuiltinDoorId.PKT_26XX,
    reportedName: "PKT 26/XX",
    propIdPrefix: DoorPropIdPrefix.DoorInteriorPocket,
    width: levelInchesNew(30),
    includeTrim: true,
    requiresDoorSwing: false,
    requiresHeightComputation: true,
  }),
  PKT_28XX: doorDescriptorNew({
    id: BuiltinDoorId.PKT_28XX,
    reportedName: "PKT 28/XX",
    propIdPrefix: DoorPropIdPrefix.DoorInteriorPocket,
    width: levelInchesNew(32),
    includeTrim: true,
    requiresDoorSwing: false,
    requiresHeightComputation: true,
  }),
  PKT_30XX: doorDescriptorNew({
    id: BuiltinDoorId.PKT_30XX,
    reportedName: "PKT 30/XX",
    propIdPrefix: DoorPropIdPrefix.DoorInteriorPocket,
    width: levelInchesNew(36),
    includeTrim: true,
    requiresDoorSwing: false,
    requiresHeightComputation: true,
  }),
  PKT_32XX: doorDescriptorNew({
    id: BuiltinDoorId.PKT_32XX,
    reportedName: "PKT 32/XX",
    propIdPrefix: DoorPropIdPrefix.DoorInteriorPocket,
    width: levelInchesNew(38),
    includeTrim: true,
    requiresDoorSwing: false,
    requiresHeightComputation: true,
  }),
  BARN_30XX: doorDescriptorNew({
    id: BuiltinDoorId.BARN_30XX,
    reportedName: "BARN 30/XX",
    propIdPrefix: DoorPropIdPrefix.DoorInteriorBarn,
    operationType: DoorOperationType.WALL_MOUNTED,
    width: levelInchesNew(36),
    requiresDoorSwing: true,
    doorLeafCount: 1,
    includeTrim: false,
    requiresHeightComputation: true,
  }),
  BARN_DBL_3068: doorDescriptorNew({
    id: BuiltinDoorId.BARN_DBL_3068,
    reportedName: "BARN DBL 30/68",
    propId: BuiltInInteriorPropId.DOORINTERIORBARNDOUBLE3068,
    operationType: DoorOperationType.WALL_MOUNTED,
    width: levelInchesNew(60),
    height: levelInchesNew(80),
    requiresDoorSwing: false,
    swingSide: SwingSide.NONE,
    doorLeafCount: 2,
    includeTrim: false,
  }),
  BARN_DBL_3080: doorDescriptorNew({
    id: BuiltinDoorId.BARN_DBL_3080,
    reportedName: "BARN DBL 30/80",
    propId: BuiltInInteriorPropId.DOORINTERIORBARNDOUBLE3080,
    operationType: DoorOperationType.WALL_MOUNTED,
    width: levelInchesNew(60),
    height: levelInchesNew(96),
    requiresDoorSwing: false,
    swingSide: SwingSide.NONE,
    doorLeafCount: 2,
    includeTrim: false,
  }),
  BI_40XX: doorDescriptorNew({
    id: BuiltinDoorId.BI_40XX,
    reportedName: "BI-HINGE 40/XX",
    propIdPrefix: DoorPropIdPrefix.DoorInteriorBiFold,
    operationType: DoorOperationType.BI_FOLD,
    width: levelInchesNew(48),
    doorLeafCount: 2,
    includeTrim: true,
    requiresHeightComputation: true,
  }),
  BI_4068: doorDescriptorNew({
    id: BuiltinDoorId.BI_4068,
    reportedName: "BI-HINGE 40/68",
    propIdPrefix: DoorPropIdPrefix.DoorInteriorBiFold,
    propId: BuiltInInteriorPropId.DOORINTERIORBIFOLD4068,
    operationType: DoorOperationType.BI_FOLD,
    width: levelInchesNew(48),
    height: levelInchesNew(80),
    doorLeafCount: 2,
    includeTrim: true,
  }),
  FIREPLACE: doorDescriptorNew({
    id: BuiltinDoorId.FIREPLACE,
    reportedName: "FIREPLACE",
    operationType: DoorOperationType.FIREPLACE,
    width: levelInchesNew(36),
    height: levelInchesNew(29),
    requiresDoorSwing: false,
  }),
  CL_BYPASS: doorDescriptorNew({
    id: BuiltinDoorId.CL_BYPASS,
    reportedName: "CL_BYPASS",
    operationType: DoorOperationType.BYPASS,
    width: levelInchesNew(30),
    height: levelInchesNew(80),
    requiresDoorSwing: false,
  }),
  EXT_DBL: doorDescriptorNew({
    id: BuiltinDoorId.EXT_DBL,
    reportedName: "50/80_DBL",
    propId: BuiltInExteriorPropId.DOORDOUBLEGLASS,
    operationType: DoorOperationType.DOUBLE,
    width: levelInchesNew(60),
    height: levelInchesNew(96),
    isExterior: true,
    doorLeafCount: 2,
    includeTrim: true,
  }),
  EXT_DBL_LOW: doorDescriptorNew({
    id: BuiltinDoorId.EXT_DBL_LOW,
    reportedName: "50/68_DBL",
    propId: BuiltInExteriorPropId.DOORDOUBLEGLASSLOW,
    operationType: DoorOperationType.DOUBLE,
    width: levelInchesNew(60),
    height: levelInchesNew(80),
    isExterior: true,
    doorLeafCount: 2,
    includeTrim: true,
  }),
  EXT_DBL60_HF: doorDescriptorNew({
    id: BuiltinDoorId.EXT_DBL60_HF,
    reportedName: "60/80_HINGED/FIXED",
    propId: BuiltInExteriorPropId.DOORDOUBLEGLASS36HINGEDFIXED,
    operationType: DoorOperationType.DOUBLE_FIXED_PANEL,
    width: levelInchesNew(72),
    height: levelInchesNew(96),
    isExterior: true,
    doorLeafCount: 2,
    includeTrim: true,
  }),
  EXT_DBL60_LOW: doorDescriptorNew({
    id: BuiltinDoorId.EXT_DBL60_LOW,
    reportedName: "60/68_DBL",
    propId: BuiltInExteriorPropId.DOORDOUBLEGLASS36LOW,
    operationType: DoorOperationType.DOUBLE,
    width: levelInchesNew(72),
    height: levelInchesNew(80),
    isExterior: true,
    doorLeafCount: 2,
    includeTrim: true,
  }),
  EXT_DBL60_LOW_HF: doorDescriptorNew({
    id: BuiltinDoorId.EXT_DBL60_LOW_HF,
    reportedName: "60/68_HINGED/FIXED",
    propId: BuiltInExteriorPropId.DOORDOUBLEGLASS36LOWHINGEDFIXED,
    operationType: DoorOperationType.DOUBLE_FIXED_PANEL,
    width: levelInchesNew(72),
    height: levelInchesNew(80),
    isExterior: true,
    doorLeafCount: 2,
    includeTrim: true,
  }),

  EXT_SLIDE: doorDescriptorNew({
    id: BuiltinDoorId.EXT_SLIDE,
    reportedName: "50/80_DBL",
    propId: BuiltInExteriorPropId.DOORDOUBLESLIDE,
    operationType: DoorOperationType.BYPASS,
    width: levelInchesNew(60),
    height: levelInchesNew(96),
    isExterior: true,
    doorLeafCount: 2,
    includeTrim: true,
  }),
  EXT_SLIDE_9068: doorDescriptorNew({
    id: BuiltinDoorId.EXT_SLIDE_9068,
    reportedName: "90/68_SLIDE",
    propIdPrefix: DoorPropIdPrefix.DoorSlidingGlass,
    operationType: DoorOperationType.BYPASS,
    width: levelInchesNew(108),
    height: levelInchesNew(80),
    isExterior: true,
    doorLeafCount: 2,
    includeTrim: true,
  }),
  EXT_SLIDE_9080: doorDescriptorNew({
    id: BuiltinDoorId.EXT_SLIDE_9080,
    reportedName: "90/80_SLIDE",
    propIdPrefix: DoorPropIdPrefix.DoorSlidingGlass,
    operationType: DoorOperationType.BYPASS,
    width: levelInchesNew(108),
    height: levelInchesNew(96),
    isExterior: true,
    doorLeafCount: 2,
    includeTrim: true,
  }),
  EXT_SLIDE_10068: doorDescriptorNew({
    id: BuiltinDoorId.EXT_SLIDE_10068,
    reportedName: "100/68_SLIDE",
    propIdPrefix: DoorPropIdPrefix.DoorSlidingGlass,
    operationType: DoorOperationType.BYPASS,
    width: levelInchesNew(120),
    height: levelInchesNew(80),
    isExterior: true,
    doorLeafCount: 4,
    includeTrim: true,
  }),
  EXT_SLIDE_10080: doorDescriptorNew({
    id: BuiltinDoorId.EXT_SLIDE_10080,
    reportedName: "100/80_SLIDE",
    propIdPrefix: DoorPropIdPrefix.DoorSlidingGlass,
    operationType: DoorOperationType.BYPASS,
    width: levelInchesNew(120),
    height: levelInchesNew(96),
    isExterior: true,
    doorLeafCount: 4,
    includeTrim: true,
  }),
  EXT_SLIDE_15068: doorDescriptorNew({
    id: BuiltinDoorId.EXT_SLIDE_15068,
    reportedName: "150/68_SLIDE",
    propIdPrefix: DoorPropIdPrefix.DoorSlidingGlass,
    operationType: DoorOperationType.BYPASS,
    width: levelInchesNew(180),
    height: levelInchesNew(80),
    isExterior: true,
    doorLeafCount: 3,
    includeTrim: true,
  }),
  EXT_SLIDE_15080: doorDescriptorNew({
    id: BuiltinDoorId.EXT_SLIDE_15080,
    reportedName: "150/80_SLIDE",
    propIdPrefix: DoorPropIdPrefix.DoorSlidingGlass,
    operationType: DoorOperationType.BYPASS,
    width: levelInchesNew(180),
    height: levelInchesNew(96),
    isExterior: true,
    doorLeafCount: 3,
    includeTrim: true,
  }),
  EXT_SLIDE_LOW: doorDescriptorNew({
    id: BuiltinDoorId.EXT_SLIDE_LOW,
    reportedName: "50/68_DBL",
    operationType: DoorOperationType.BYPASS,
    width: levelInchesNew(60),
    height: levelInchesNew(80),
    requiresDoorSwing: false,
    isExterior: true,
    doorLeafCount: 2,
    includeTrim: true,
  }),
  EXT_SLIDE_4068: doorDescriptorNew({
    id: BuiltinDoorId.EXT_SLIDE_4068,
    reportedName: "40/68",
    propIdPrefix: DoorPropIdPrefix.DoorSlidingGlass,
    operationType: DoorOperationType.BYPASS,
    width: levelInchesNew(48),
    height: levelInchesNew(80),
    requiresDoorSwing: false,
    isExterior: true,
    doorLeafCount: 2,
    includeTrim: true,
  }),
  EXT_SLIDE_4080: doorDescriptorNew({
    id: BuiltinDoorId.EXT_SLIDE_4080,
    reportedName: "40/68",
    propIdPrefix: DoorPropIdPrefix.DoorSlidingGlass,
    operationType: DoorOperationType.BYPASS,
    width: levelInchesNew(48),
    height: levelInchesNew(96),
    requiresDoorSwing: false,
    isExterior: true,
    doorLeafCount: 2,
    includeTrim: true,
  }),
  EXT_SLIDE_5068: doorDescriptorNew({
    id: BuiltinDoorId.EXT_SLIDE_5068,
    reportedName: "50/68",
    propIdPrefix: DoorPropIdPrefix.DoorSlidingGlass,
    operationType: DoorOperationType.BYPASS,
    width: levelInchesNew(60),
    height: levelInchesNew(80),
    requiresDoorSwing: false,
    isExterior: true,
    doorLeafCount: 2,
    includeTrim: true,
  }),
  EXT_SLIDE_5080: doorDescriptorNew({
    id: BuiltinDoorId.EXT_SLIDE_5080,
    reportedName: "50/68",
    propIdPrefix: DoorPropIdPrefix.DoorSlidingGlass,
    operationType: DoorOperationType.BYPASS,
    width: levelInchesNew(60),
    height: levelInchesNew(96),
    requiresDoorSwing: false,
    isExterior: true,
    doorLeafCount: 2,
    includeTrim: true,
  }),
  EXT_SLIDE_6068: doorDescriptorNew({
    id: BuiltinDoorId.EXT_SLIDE_6068,
    reportedName: "60/68",
    propIdPrefix: DoorPropIdPrefix.DoorSlidingGlass,
    operationType: DoorOperationType.BYPASS,
    width: levelInchesNew(72),
    height: levelInchesNew(80),
    requiresDoorSwing: false,
    isExterior: true,
    doorLeafCount: 2,
    includeTrim: true,
  }),
  EXT_SLIDE_6080: doorDescriptorNew({
    id: BuiltinDoorId.EXT_SLIDE_6080,
    reportedName: "60/80",
    propIdPrefix: DoorPropIdPrefix.DoorSlidingGlass,
    operationType: DoorOperationType.BYPASS,
    width: levelInchesNew(72),
    height: levelInchesNew(96),
    requiresDoorSwing: false,
    isExterior: true,
    doorLeafCount: 2,
    includeTrim: true,
  }),
  EXT_SLIDE_7680: doorDescriptorNew({
    id: BuiltinDoorId.EXT_SLIDE_7680,
    reportedName: "76/80_DBL",
    propIdPrefix: DoorPropIdPrefix.DoorSlidingGlass,
    operationType: DoorOperationType.BYPASS,
    width: levelInchesNew(90),
    height: levelInchesNew(96),
    requiresDoorSwing: false,
    isExterior: true,
    doorLeafCount: 2,
    includeTrim: true,
  }),
  EXT_SLIDE_7880: doorDescriptorNew({
    id: BuiltinDoorId.EXT_SLIDE_7880,
    reportedName: "78/80_DBL",
    propIdPrefix: DoorPropIdPrefix.DoorSlidingGlass,
    operationType: DoorOperationType.BYPASS,
    width: levelInchesNew(92),
    height: levelInchesNew(96),
    requiresDoorSwing: false,
    isExterior: true,
    doorLeafCount: 2,
    includeTrim: true,
  }),
  EXT_SLIDE_8080: doorDescriptorNew({
    id: BuiltinDoorId.EXT_SLIDE_8080,
    reportedName: "80/80_DBL",
    propIdPrefix: DoorPropIdPrefix.DoorSlidingGlass,
    operationType: DoorOperationType.BYPASS,
    width: levelInchesNew(96),
    height: levelInchesNew(96),
    requiresDoorSwing: false,
    isExterior: true,
    doorLeafCount: 2,
    includeTrim: true,
  }),
  EXT_SLIDE_12080: doorDescriptorNew({
    id: BuiltinDoorId.EXT_SLIDE_12080,
    reportedName: "120/80_QUAD",
    propId: BuiltInExteriorPropId.DOORSLIDINGGLASS12080,
    operationType: DoorOperationType.BYPASS,
    width: levelInchesNew(120),
    height: levelInchesNew(96),
    requiresDoorSwing: false,
    isExterior: true,
    doorLeafCount: 4,
    includeTrim: true,
  }),
  EXT_SLIDE_12080_3: doorDescriptorNew({
    id: BuiltinDoorId.EXT_SLIDE_12080_3,
    reportedName: "120/80_TRIPLE",
    propId: "doorSlidingGlass12080TripleNoGrid",
    operationType: DoorOperationType.BYPASS,
    width: levelInchesNew(120),
    height: levelInchesNew(96),
    requiresDoorSwing: false,
    isExterior: true,
    doorLeafCount: 3,
    includeTrim: true,
  }),
  EXT_SGL28: doorDescriptorNew({
    id: BuiltinDoorId.EXT_SGL28,
    reportedName: "28/80",
    propId: BuiltInExteriorPropId.DOORSINGLEGLASS32,
    width: levelInchesNew(32),
    height: levelInchesNew(96),
    isExterior: true,
    includeTrim: true,
  }),
  EXT_SGL28_HL: doorDescriptorNew({
    id: BuiltinDoorId.EXT_SGL28_HL,
    reportedName: "28/80 H.L",
    propId: BuiltInExteriorPropId.DOORSINGLEGLASS32HL,
    width: levelInchesNew(32),
    height: levelInchesNew(96),
    isExterior: true,
    includeTrim: true,
  }),
  EXT_SGL28_LOW: doorDescriptorNew({
    id: BuiltinDoorId.EXT_SGL28_LOW,
    reportedName: "28/68",
    propId: BuiltInExteriorPropId.DOORSINGLEGLASS32LOW,
    width: levelInchesNew(32),
    height: levelInchesNew(80),
    isExterior: true,
    includeTrim: true,
  }),
  EXT_SGL28_LOW_HL: doorDescriptorNew({
    id: BuiltinDoorId.EXT_SGL28_LOW_HL,
    reportedName: "28/68 H.L",
    propId: BuiltInExteriorPropId.DOORSINGLEGLASS32LOWHL,
    width: levelInchesNew(32),
    height: levelInchesNew(80),
    isExterior: true,
    includeTrim: true,
  }),
  EXT_SGL: doorDescriptorNew({
    id: BuiltinDoorId.EXT_SGL,
    reportedName: "30/80",
    propId: BuiltInExteriorPropId.DOORSINGLEGLASS,
    width: levelInchesNew(36),
    height: levelInchesNew(96),
    isPrimaryDoor: true,
    isExterior: true,
    includeTrim: true,
  }),
  EXT_SGL_HL: doorDescriptorNew({
    id: BuiltinDoorId.EXT_SGL_HL,
    reportedName: "30/80 H.L",
    propId: BuiltInExteriorPropId.DOORSINGLEGLASS36HL,
    width: levelInchesNew(36),
    height: levelInchesNew(96),
    isExterior: true,
    includeTrim: true,
  }),
  EXT_SGL_LOW_HL: doorDescriptorNew({
    id: BuiltinDoorId.EXT_SGL_LOW_HL,
    reportedName: "30/68 H.L",
    propId: BuiltInExteriorPropId.DOORSINGLEGLASS36LOWHL,
    width: levelInchesNew(36),
    height: levelInchesNew(80),
    isExterior: true,
    includeTrim: true,
  }),
  EXT_SGL_ARC: doorDescriptorNew({
    id: BuiltinDoorId.EXT_SGL_ARC,
    reportedName: "30/80 ARCH",
    operationType: DoorOperationType.ARC,
    width: levelInchesNew(36),
    height: levelInchesNew(96),
    isExterior: true,
    swingSide: SwingSide.RIGHT,
    isPrimaryDoor: true,
  }),
  EXT_SGL_ARC_36: doorDescriptorNew({
    id: BuiltinDoorId.EXT_SGL_ARC_36,
    reportedName: "36/80 ARCH",
    propId: BuiltInExteriorPropId.DOORSINGLEARCH36,
    operationType: DoorOperationType.ARC,
    width: levelInchesNew(42),
    height: levelInchesNew(96),
    isExterior: true,
    segmentedArc: true,
    arcFactor: 1 / 3,
  }),
  EXT_SGL_PORTICO: doorDescriptorNew({
    id: BuiltinDoorId.EXT_SGL_PORTICO,
    reportedName: "50/130 ARC",
    operationType: DoorOperationType.PORTICO,
    width: levelInchesNew(60),
    height: levelInchesNew(156),
    isExterior: true,
    segmentedArc: true,
    arcFactor: 1 / 2,
    arcDepth: 12,
    requiresDoorSwing: true,
    swingSide: SwingSide.RIGHT,
  }),
  EXT_SGL_PORTICO_96_LOW_ARC: doorDescriptorNew({
    id: BuiltinDoorId.EXT_SGL_PORTICO_96_LOW_ARC,
    reportedName: "50/96 ARC",
    operationType: DoorOperationType.PORTICO,
    width: levelInchesNew(60),
    height: levelInchesNew(114),
    isExterior: true,
    segmentedArc: true,
    arcFactor: 1 / 4,
    arcDepth: 12,
    requiresDoorSwing: true,
    swingSide: SwingSide.RIGHT,
  }),
  EXT_SGL_PORTICO_104_LOW_ARC: doorDescriptorNew({
    id: BuiltinDoorId.EXT_SGL_PORTICO_104_LOW_ARC,
    reportedName: "50/104 ARC",
    operationType: DoorOperationType.PORTICO,
    width: levelInchesNew(60),
    height: levelInchesNew(124),
    isExterior: true,
    segmentedArc: true,
    arcFactor: 1 / 4,
    arcDepth: 12,
    requiresDoorSwing: true,
    swingSide: SwingSide.RIGHT,
  }),
  EXT_SGL_PORTICO_46_100_LOW_ARC: doorDescriptorNew({
    id: BuiltinDoorId.EXT_SGL_PORTICO_46_100_LOW_ARC,
    reportedName: "46/100 ARC",
    operationType: DoorOperationType.PORTICO,
    width: levelInchesNew(54),
    height: levelInchesNew(120),
    isExterior: true,
    segmentedArc: true,
    arcFactor: 1 / 4,
    arcDepth: 12,
    requiresDoorSwing: true,
    swingSide: SwingSide.RIGHT,
  }),
  EXT_SGL_PORTICO_46_110_LOW_ARC: doorDescriptorNew({
    id: BuiltinDoorId.EXT_SGL_PORTICO_46_110_LOW_ARC,
    reportedName: "46/110 ARC",
    operationType: DoorOperationType.PORTICO,
    width: levelInchesNew(54),
    height: levelInchesNew(132),
    isExterior: true,
    segmentedArc: true,
    arcFactor: 1 / 4,
    arcDepth: 12,
    requiresDoorSwing: true,
    swingSide: SwingSide.RIGHT,
  }),
  EXT_SGL_PORTICO_118_LOW_ARC: doorDescriptorNew({
    id: BuiltinDoorId.EXT_SGL_PORTICO_118_LOW_ARC,
    reportedName: "50/118 ARC",
    operationType: DoorOperationType.PORTICO,
    width: levelInchesNew(50),
    height: levelInchesNew(140),
    isExterior: true,
    segmentedArc: true,
    arcFactor: 1 / 4,
    arcDepth: 12,
    requiresDoorSwing: true,
    swingSide: SwingSide.RIGHT,
  }),
  EXT_SGL_LOW: doorDescriptorNew({
    id: BuiltinDoorId.EXT_SGL_LOW,
    reportedName: "30/68",
    propId: BuiltInExteriorPropId.DOORSINGLEGLASSLOW,
    width: levelInchesNew(36),
    isPrimaryDoor: true,
    height: levelInchesNew(80),
    isExterior: true,
    includeTrim: true,
  }),
  EXT_SGL_SEC: doorDescriptorNew({
    id: BuiltinDoorId.EXT_SGL_SEC,
    reportedName: "30/80",
    propId: BuiltInExteriorPropId.DOORSINGLEGLASSSECONDARY,
    width: levelInchesNew(36),
    height: levelInchesNew(96),
    isExterior: true,
    includeTrim: true,
  }),
  EXT_SGL_LOW_SEC: doorDescriptorNew({
    id: BuiltinDoorId.EXT_SGL_LOW_SEC,
    reportedName: "30/68",
    propId: BuiltInExteriorPropId.DOORSINGLEGLASSLOWSECONDARY,
    width: levelInchesNew(36),
    height: levelInchesNew(80),
    isExterior: true,
    includeTrim: true,
  }),
  EXT_SGL_LOW_TRANSOM: doorDescriptorNew({
    id: BuiltinDoorId.EXT_SGL_LOW_TRANSOM,
    reportedName: "30/68 & 30/10 TRANSOM",
    propId: BuiltInExteriorPropId.DOORSINGLEGLASSLOWTRANSOM,
    width: levelInchesNew(36),
    height: levelInchesNew(92),
    isExterior: true,
    includeTrim: true,
  }),
  EXT_SGL36: doorDescriptorNew({
    id: BuiltinDoorId.EXT_SGL36,
    reportedName: "36/80",
    propId: BuiltInExteriorPropId.DOORSINGLEGLASS36,
    width: levelInchesNew(42),
    height: levelInchesNew(96),
    isExterior: true,
    includeTrim: true,
  }),
  OPEN_2036: doorDescriptorNew({
    id: BuiltinDoorId.OPEN_2036,
    reportedName: "20/36",
    operationType: DoorOperationType.OPEN,
    width: levelInchesNew(24),
    height: levelInchesNew(42),
    swingSide: SwingSide.NONE,
  }),
  OPEN_28XX: doorDescriptorNew({
    id: BuiltinDoorId.OPEN_28XX,
    reportedName: "28/XX",
    operationType: DoorOperationType.OPEN,
    width: levelInchesNew(32),
    requiresDoorSwing: false,
    swingSide: SwingSide.NONE,
    requiresHeightComputation: true,
  }),
  OPEN_2080: doorDescriptorNew({
    id: BuiltinDoorId.OPEN_2080,
    reportedName: "20/80",
    operationType: DoorOperationType.OPEN,
    width: levelInchesNew(24),
    height: levelInchesNew(96),
    requiresDoorSwing: false,
    swingSide: SwingSide.NONE,
  }),
  OPEN_2280: doorDescriptorNew({
    id: BuiltinDoorId.OPEN_2280,
    reportedName: "22/80",
    operationType: DoorOperationType.OPEN,
    width: levelInchesNew(26),
    height: levelInchesNew(96),
    requiresDoorSwing: false,
    swingSide: SwingSide.NONE,
  }),
  OPEN_2468: doorDescriptorNew({
    id: BuiltinDoorId.OPEN_2468,
    reportedName: "24/68",
    operationType: DoorOperationType.OPEN,
    width: levelInchesNew(28),
    height: levelInchesNew(80),
    swingSide: SwingSide.NONE,
  }),
  OPEN_2668: doorDescriptorNew({
    id: BuiltinDoorId.OPEN_2668,
    reportedName: "26/68",
    operationType: DoorOperationType.OPEN,
    width: levelInchesNew(30),
    height: levelInchesNew(80),
    swingSide: SwingSide.NONE,
  }),
  OPEN_2680: doorDescriptorNew({
    id: BuiltinDoorId.OPEN_2680,
    reportedName: "26/80",
    operationType: DoorOperationType.OPEN,
    width: levelInchesNew(30),
    height: levelInchesNew(96),
    swingSide: SwingSide.NONE,
  }),
  OPEN_30XX: doorDescriptorNew({
    id: BuiltinDoorId.OPEN_30XX,
    reportedName: "30/XX",
    operationType: DoorOperationType.OPEN,
    width: levelInchesNew(36),
    requiresDoorSwing: false,
    swingSide: SwingSide.NONE,
    requiresHeightComputation: true,
  }),
  OPEN_3080: doorDescriptorNew({
    id: BuiltinDoorId.OPEN_3080,
    reportedName: "30/80",
    operationType: DoorOperationType.OPEN,
    width: levelInchesNew(36),
    height: levelInchesNew(96),
    requiresDoorSwing: false,
    swingSide: SwingSide.NONE,
  }),
  OPEN_3276: doorDescriptorNew({
    id: BuiltinDoorId.OPEN_3276,
    reportedName: "32/76",
    operationType: DoorOperationType.OPEN,
    width: levelInchesNew(38),
    height: levelInchesNew(90),
    requiresDoorSwing: false,
    swingSide: SwingSide.NONE,
  }),
  OPEN_36XX: doorDescriptorNew({
    id: BuiltinDoorId.OPEN_36XX,
    reportedName: "36/XX",
    operationType: DoorOperationType.OPEN,
    width: levelInchesNew(42),
    requiresDoorSwing: false,
    swingSide: SwingSide.NONE,
    requiresHeightComputation: true,
  }),
  OPEN_3680: doorDescriptorNew({
    id: BuiltinDoorId.OPEN_3680,
    reportedName: "36/80",
    operationType: DoorOperationType.OPEN,
    width: levelInchesNew(42),
    height: levelInchesNew(96),
    requiresDoorSwing: false,
    swingSide: SwingSide.NONE,
    requiresHeightComputation: false,
  }),
  OPEN_38XX: doorDescriptorNew({
    id: BuiltinDoorId.OPEN_38XX,
    reportedName: "38/XX",
    operationType: DoorOperationType.OPEN,
    width: levelInchesNew(44),
    requiresDoorSwing: false,
    swingSide: SwingSide.NONE,
    requiresHeightComputation: true,
  }),
  OPEN_40XX: doorDescriptorNew({
    id: BuiltinDoorId.OPEN_40XX,
    reportedName: "40/XX",
    operationType: DoorOperationType.OPEN,
    width: levelInchesNew(48),
    requiresDoorSwing: false,
    swingSide: SwingSide.NONE,
    requiresHeightComputation: true,
  }),
  OPEN_7786: doorDescriptorNew({
    id: BuiltinDoorId.OPEN_7786,
    reportedName: "77/86",
    operationType: DoorOperationType.OPEN,
    width: levelInchesNew(91),
    height: levelInchesNew(102),
    requiresDoorSwing: false,
    swingSide: SwingSide.NONE,
    requiresHeightComputation: false,
  }),
  OPEN_88XX: doorDescriptorNew({
    id: BuiltinDoorId.OPEN_88XX,
    reportedName: "88/XX",
    operationType: DoorOperationType.OPEN,
    width: levelInchesNew(104),
    requiresDoorSwing: false,
    swingSide: SwingSide.NONE,
    requiresHeightComputation: true,
  }),
  OPEN_15080: doorDescriptorNew({
    id: BuiltinDoorId.OPEN_15080,
    reportedName: "150/80",
    operationType: DoorOperationType.OPEN,
    width: levelInchesNew(180),
    height: levelInchesNew(96),
    requiresDoorSwing: false,
    swingSide: SwingSide.NONE,
  }),
  OPEN_ARC_3080: doorDescriptorNew({
    id: BuiltinDoorId.OPEN_ARC_3080,
    reportedName: "ARCH 30/80",
    operationType: DoorOperationType.ARC,
    width: levelInchesNew(36),
    height: levelInchesNew(96),
    requiresDoorSwing: false,
    swingSide: SwingSide.NONE,
  }),
  OPEN_ARC_3468: doorDescriptorNew({
    id: BuiltinDoorId.OPEN_ARC_3468,
    reportedName: "ARCH 34/68",
    operationType: DoorOperationType.ARC,
    width: levelInchesNew(40),
    height: levelInchesNew(80),
    requiresDoorSwing: false,
    swingSide: SwingSide.NONE,
  }),
  OPEN_ARC_3478: doorDescriptorNew({
    id: BuiltinDoorId.OPEN_ARC_3478,
    reportedName: "ARCH 34/78",
    operationType: DoorOperationType.ARC,
    width: levelInchesNew(40),
    height: levelInchesNew(92),
    requiresDoorSwing: false,
    swingSide: SwingSide.NONE,
  }),
  OPEN_ARC_4080: doorDescriptorNew({
    id: BuiltinDoorId.OPEN_ARC_4080,
    reportedName: "ARCH 40/80",
    operationType: DoorOperationType.ARC,
    width: levelInchesNew(48),
    height: levelInchesNew(96),
    requiresDoorSwing: false,
    swingSide: SwingSide.NONE,
  }),
  OPEN_ARC_6080: doorDescriptorNew({
    id: BuiltinDoorId.OPEN_ARC_6080,
    reportedName: "ARCH 60/80",
    operationType: DoorOperationType.ARC,
    width: levelInchesNew(72),
    height: levelInchesNew(96),
    requiresDoorSwing: false,
    swingSide: SwingSide.NONE,
  }),
  OPEN_ARC_7786: doorDescriptorNew({
    id: BuiltinDoorId.OPEN_ARC_7786,
    reportedName: "ARCH 77/86",
    operationType: DoorOperationType.ARC,
    width: levelInchesNew(91),
    height: levelInchesNew(102),
    requiresDoorSwing: false,
    swingSide: SwingSide.NONE,
  }),
  OPEN_ARC_FULL: doorDescriptorNew({
    id: BuiltinDoorId.OPEN_ARC_FULL,
    reportedName: "ARCH FULL",
    operationType: DoorOperationType.ARC,
    height: levelInchesNew(96),
    requiresDoorSwing: false,
    swingSide: SwingSide.NONE,
  }),
  OPEN_5068: doorDescriptorNew({
    id: BuiltinDoorId.OPEN_5068,
    reportedName: "50/68",
    operationType: DoorOperationType.OPEN,
    width: levelInchesNew(60),
    height: levelInchesNew(80),
    requiresDoorSwing: false,
    swingSide: SwingSide.NONE,
  }),
  OPEN_5080: doorDescriptorNew({
    id: BuiltinDoorId.OPEN_5080,
    reportedName: "50/80",
    operationType: DoorOperationType.OPEN,
    width: levelInchesNew(60),
    height: levelInchesNew(96),
    requiresDoorSwing: false,
    swingSide: SwingSide.NONE,
  }),
  OPEN_50XX: doorDescriptorNew({
    id: BuiltinDoorId.OPEN_50XX,
    reportedName: "50/XX",
    operationType: DoorOperationType.OPEN,
    width: levelInchesNew(60),
    requiresDoorSwing: false,
    swingSide: SwingSide.NONE,
    requiresHeightComputation: true,
  }),
  OPEN_60XX: doorDescriptorNew({
    id: BuiltinDoorId.OPEN_60XX,
    reportedName: "60/XX",
    operationType: DoorOperationType.OPEN,
    width: levelInchesNew(72),
    requiresDoorSwing: false,
    swingSide: SwingSide.NONE,
    requiresHeightComputation: true,
  }),
  OPEN_30710: doorDescriptorNew({
    id: BuiltinDoorId.OPEN_30710,
    reportedName: "30/710",
    operationType: DoorOperationType.OPEN,
    width: levelInchesNew(36),
    height: levelInchesNew(94),
    requiresDoorSwing: false,
    swingSide: SwingSide.NONE,
  }),
  OPEN_36710: doorDescriptorNew({
    id: BuiltinDoorId.OPEN_36710,
    reportedName: "36/710",
    operationType: DoorOperationType.OPEN,
    width: levelInchesNew(42),
    height: levelInchesNew(94),
    requiresDoorSwing: false,
    swingSide: SwingSide.NONE,
  }),
  OPEN_40710: doorDescriptorNew({
    id: BuiltinDoorId.OPEN_40710,
    reportedName: "40/710",
    operationType: DoorOperationType.OPEN,
    width: levelInchesNew(48),
    height: levelInchesNew(94),
    requiresDoorSwing: false,
    swingSide: SwingSide.NONE,
  }),
  OPEN_80710: doorDescriptorNew({
    id: BuiltinDoorId.OPEN_80710,
    reportedName: "80/710",
    operationType: DoorOperationType.OPEN,
    width: levelInchesNew(96),
    height: levelInchesNew(94),
    requiresDoorSwing: false,
    swingSide: SwingSide.NONE,
  }),
  OPEN_120XX: doorDescriptorNew({
    id: BuiltinDoorId.OPEN_120XX,
    reportedName: "120/XX",
    operationType: DoorOperationType.OPEN,
    width: levelInchesNew(144),
    requiresDoorSwing: false,
    swingSide: SwingSide.NONE,
    requiresHeightComputation: true,
  }),
  OPEN_12068: doorDescriptorNew({
    id: BuiltinDoorId.OPEN_12068,
    reportedName: "120/68",
    operationType: DoorOperationType.OPEN,
    width: levelInchesNew(144),
    height: levelInchesNew(80),
    requiresDoorSwing: false,
    swingSide: SwingSide.NONE,
  }),
  OPEN_12080: doorDescriptorNew({
    id: BuiltinDoorId.OPEN_12080,
    reportedName: "120/80",
    operationType: DoorOperationType.OPEN,
    width: levelInchesNew(144),
    height: levelInchesNew(96),
    requiresDoorSwing: false,
    swingSide: SwingSide.NONE,
  }),
  OPEN_186_74: doorDescriptorNew({
    id: BuiltinDoorId.OPEN_186_74,
    reportedName: "18' - 6 1/2\" / 7' - 4\"",
    operationType: DoorOperationType.OPEN,
    width: levelInchesNew(222.5),
    height: levelInchesNew(88),
    requiresDoorSwing: false,
    swingSide: SwingSide.NONE,
  }),
  OPEN_811_710: doorDescriptorNew({
    id: BuiltinDoorId.OPEN_811_710,
    reportedName: "811/710",
    operationType: DoorOperationType.OPEN,
    width: levelInchesNew(107),
    height: levelInchesNew(94),
    requiresDoorSwing: false,
    swingSide: SwingSide.NONE,
  }),
  OPEN_FULL: doorDescriptorNew({
    id: BuiltinDoorId.OPEN_FULL,
    reportedName: "OPEN_FULL",
    operationType: DoorOperationType.OPEN,
    requiresDoorSwing: false,
    swingSide: SwingSide.NONE,
    height: levelInchesNew(12), // silences warning having to do with missing height field
  }),
  G_20080: doorDescriptorNew({
    id: BuiltinDoorId.G_20080,
    reportedName: "20/0 X 8/0 GARAGE DOOR",
    operationType: DoorOperationType.GARAGE_DOUBLE,
    propIdPrefix: DoorPropIdPrefix.DoorGarage,
    roughOpeningAdjustment: 1.5,
    width: levelInchesNew(240),
    height: levelInchesNew(96),
    includeTrim: true,
    isExterior: true,
    includeDriveway: true,
  }),
  G_10080: doorDescriptorNew({
    id: BuiltinDoorId.G_10080,
    reportedName: "10/0 X 8/0 GARAGE DOOR",
    propIdPrefix: DoorPropIdPrefix.DoorGarage,
    operationType: DoorOperationType.GARAGE_SINGLE,
    roughOpeningAdjustment: 1.5,
    width: levelInchesNew(120),
    height: levelInchesNew(96),
    includeTrim: true,
    isExterior: true,
    includeDriveway: true,
  }),
  G1: doorDescriptorNew({
    id: BuiltinDoorId.G1,
    reportedName: "8/0 X 7/0 GARAGE DOOR",
    propId: BuiltInExteriorPropId.GARAGEDOORSINGLE,
    operationType: DoorOperationType.GARAGE_SINGLE,
    roughOpeningAdjustment: 1.5,
    width: levelInchesNew(96),
    height: levelInchesNew(84),
    includeTrim: true,
    isExterior: true,
    includeDriveway: true,
  }),
  G1_2: doorDescriptorNew({
    id: BuiltinDoorId.G1_2,
    reportedName: "8/0 X 7/0 GARAGE DOOR",
    propId: BuiltInExteriorPropId.GARAGEDOORSINGLESECONDARY,
    operationType: DoorOperationType.GARAGE_SINGLE,
    roughOpeningAdjustment: 1.5,
    width: levelInchesNew(96),
    height: levelInchesNew(84),
    includeTrim: true,
    isExterior: true,
    includeDriveway: true,
  }),
  G2: doorDescriptorNew({
    id: BuiltinDoorId.G2,
    reportedName: "16/0 X 7/0 GARAGE DOOR",
    propId: BuiltInExteriorPropId.GARAGEDOORDOUBLE,
    operationType: DoorOperationType.GARAGE_DOUBLE,
    width: GARAGE_TWO_WIDTH,
    roughOpeningAdjustment: 1.5,
    height: levelInchesNew(84),
    includeTrim: true,
    isExterior: true,
    includeDriveway: true,
  }),
  G2_2: doorDescriptorNew({
    id: BuiltinDoorId.G2_2,
    reportedName: "16/0 X 7/0 GARAGE DOOR",
    propId: BuiltInExteriorPropId.GARAGEDOORDOUBLESECONDARY,
    operationType: DoorOperationType.GARAGE_DOUBLE,
    width: GARAGE_TWO_WIDTH,
    roughOpeningAdjustment: 1.5,
    height: levelInchesNew(84),
    includeTrim: true,
    isExterior: true,
    includeDriveway: true,
  }),
  G2_3: doorDescriptorNew({
    id: BuiltinDoorId.G2_3,
    reportedName: "16/0 X 7/0 GARAGE DOOR",
    propId: BuiltInExteriorPropId.GARAGEDOORDOUBLETERTIARY,
    operationType: DoorOperationType.GARAGE_DOUBLE,
    width: GARAGE_TWO_WIDTH,
    roughOpeningAdjustment: 1.5,
    height: levelInchesNew(84),
    includeTrim: true,

    isExterior: true,
    includeDriveway: true,
  }),
  G2_4: doorDescriptorNew({
    id: BuiltinDoorId.G2_4,
    reportedName: "16/0 X 7/0 GARAGE DOOR",
    propId: BuiltInExteriorPropId.GARAGEDOORDOUBLEQUATERNARY,
    operationType: DoorOperationType.GARAGE_DOUBLE,
    width: GARAGE_TWO_WIDTH,
    roughOpeningAdjustment: 1.5,
    height: levelInchesNew(84),
    includeTrim: true,
    isExterior: true,
    includeDriveway: true,
  }),
  G_9070: doorDescriptorNew({
    id: BuiltinDoorId.G_9070,
    reportedName: "9/0 X 7/0 GARAGE DOOR",
    propIdPrefix: DoorPropIdPrefix.DoorGarage,
    operationType: DoorOperationType.GARAGE_SINGLE,
    roughOpeningAdjustment: 1.5,
    width: levelInchesNew(108),
    height: levelInchesNew(84),
    includeTrim: true,
    isExterior: true,
    includeDriveway: true,
  }),
  G_18070: doorDescriptorNew({
    id: BuiltinDoorId.G_18070,
    reportedName: "18/0 X 7/0 GARAGE DOOR",
    propIdPrefix: DoorPropIdPrefix.DoorGarage,
    propId: BuiltInExteriorPropId.DOORGARAGE18070,
    operationType: DoorOperationType.GARAGE_DOUBLE,
    roughOpeningAdjustment: 1.5,
    width: levelInchesNew(216),
    height: levelInchesNew(84),
    includeTrim: true,
    isExterior: true,
    includeDriveway: true,
  }),

  G_18070_2: doorDescriptorNew({
    id: BuiltinDoorId.G_18070_2,
    reportedName: "18/0 X 7/0 GARAGE DOOR",
    propId: BuiltInExteriorPropId.DOORGARAGE18070SECONDARY,
    operationType: DoorOperationType.GARAGE_DOUBLE,
    roughOpeningAdjustment: 1.5,
    width: levelInchesNew(216),
    height: levelInchesNew(84),
    includeTrim: true,
    isExterior: true,
    includeDriveway: true,
  }),
  NONE: doorDescriptorNew({
    id: BuiltinDoorId.NONE,
    reportedName: "None",
    operationType: DoorOperationType.OPEN,
    width: levelInchesNew(0),
    height: levelInchesNew(0),
    requiresDoorSwing: false,
  }),
};

export const defaultDoorTypes: Table<DoorDescriptor> = {
  "Default Catalog Door": doorDescriptorNew({
    id: "Default Catalog Door",
    name: "Default Catalog Door",
    reportedName: "26/68",
    width: levelInchesNew(30),
    height: levelInchesNew(80),
    includeTrim: true,
  }),
};

export function getDoorCatalog(doorCatalogSettings: DoorCatalogSettings): Table<DoorDescriptor> {
  const doorCatalog = doorCatalogSettings?.doorTypes ?? defaultDoorTypes;

  return { ...doorCatalog, ...defaultDoorCatalog };
}

export function getDoorCatalogsForUI(doorCatalogSettings: DoorCatalogSettings): Table<DoorDescriptor> {
  const doorCatalog = doorCatalogSettings?.doorTypes ?? defaultDoorTypes;

  if (doorCatalogSettings.displayBuiltInCatalog) {
    return { ...doorCatalog, ...defaultDoorCatalog };
  } else {
    return { ...doorCatalog };
  }
}

// return custom catalog or default but not both
export function getDoorCatalogOrDefault(doorCatalogSettings: DoorCatalogSettings): Table<DoorDescriptor> {
  if (objectIsEmpty(doorCatalogSettings.doorTypes)) {
    return { ...defaultDoorCatalog };
  }

  return doorCatalogSettings.doorTypes;
}
